import { useSignalLayer } from '../../hooks/useSignalLayer'
import { NavFn } from '../../pages/SchedulerPage'
import { SHIP_GANTT_CHIP_LABELS } from '../../constants/ship.logic'
import { FilterChip, Segmented } from '@indipia/ui-core'
import { SignalToggleChips } from '../SignalControls'
import { GANTT_PERIOD_OPTIONS } from '../../constants/gantt.const'
import { SCHEDULE_VIEW_OPTIONS } from '../../views/scheduleUtils'
// 옵션줄(칩 · 범위 · 보기 세그먼트)

export type GanttOptionsBarProps = {
  showRel: boolean
  setShowRel: React.Dispatch<React.SetStateAction<boolean>>
  showCont: boolean
  toggleContChip: (_u?: unknown) => void
  showVendor: boolean
  toggleVendorChip: (_u?: unknown) => void
  showMarkers: boolean
  setShowMarkers: React.Dispatch<React.SetStateAction<boolean>>
  showFloat: boolean
  setShowFloat: React.Dispatch<React.SetStateAction<boolean>>
  sigOn: boolean
  hideBars: boolean
  setHideBars: React.Dispatch<React.SetStateAction<boolean>>
  signal: ReturnType<typeof useSignalLayer>
  /** 공정 상세창이 열려 있으면 '보기' 구간을 숨긴다(F-10) */
  panelOpen: boolean
  ganttPeriod: "전체" | "1주" | "2주" | "월"
  setGanttPeriod: React.Dispatch<React.SetStateAction<"전체" | "1주" | "2주" | "월">>
  navigate: NavFn
}

export function GanttOptionsBar({
  showRel, setShowRel, showCont, toggleContChip, showVendor, toggleVendorChip, showMarkers, setShowMarkers,
  showFloat, setShowFloat, sigOn, hideBars, setHideBars, signal, panelOpen, ganttPeriod, setGanttPeriod, navigate,
}: GanttOptionsBarProps) {
  return (
    <div style={{ background: 'var(--shell-bg)', padding: '6px 18px', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
      {/* 칩 그룹 — 바 숨기기까지 한 덩어리. 간격은 --spacing-02(4px)로 Options bar 전체 gap 8과 분리 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-02)', flexShrink: 0 }}>
        {([
          // lock = 신호등 켜짐 중 잠기는 칩. 기획 열거(선후관계·시공파트너·자재파트너·대기일·바 숨기기)만 잠그고 [발주·입고]는 켜짐 중에도 활성(C-14)
          { label: '선후관계', val: showRel,     set: setShowRel,     icon: null, lock: true },
          { label: SHIP_GANTT_CHIP_LABELS.foreman, val: showCont,    set: toggleContChip,   icon: null, lock: true },
          { label: SHIP_GANTT_CHIP_LABELS.vendor, val: showVendor,  set: toggleVendorChip, icon: null, lock: true },
          { label: '발주·입고', val: showMarkers, set: setShowMarkers, icon: <svg key="ms" width="7" height="7" viewBox="0 0 12 12"><polygon points="6,0 12,6 6,12 0,6" fill="currentColor"/></svg>, lock: false },
          { label: '대기일',   val: showFloat,   set: setShowFloat,   icon: null, lock: true },
        ] as const).map(({ label, val, set, icon, lock }) => (
          <FilterChip key={label} size="lg" active={lock ? val && !sigOn : val} disabled={lock && sigOn} onClick={() => set((v: boolean) => !v)}>
            {icon}{label}
          </FilterChip>
        ))}
        <FilterChip size="lg" active={hideBars && !sigOn} disabled={sigOn} onClick={() => setHideBars(v => !v)}>
          {hideBars ? (
            <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>바 보기</>
          ) : (
            <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>바 숨기기</>
          )}
        </FilterChip>
        <div style={{ width: 1, height: 16, background: 'var(--shell-border)', margin: '0 4px', flexShrink: 0 }} />
        <SignalToggleChips layer={signal} />
      </div>
      <div style={{ flex: 1 }} />
      <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', letterSpacing: '.3px', whiteSpace: 'nowrap' }}>범위</span>
      <Segmented className="!shrink-0" options={GANTT_PERIOD_OPTIONS} value={ganttPeriod} onChange={v => setGanttPeriod(v as typeof ganttPeriod)} />
      {!panelOpen && (
        <>
          <div style={{ width: 1, height: 16, background: 'var(--shell-border)', margin: '0 2px', flexShrink: 0 }} />
          <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', letterSpacing: '.3px', whiteSpace: 'nowrap' }}>보기</span>
          <Segmented
            className="!shrink-0"
            options={SCHEDULE_VIEW_OPTIONS}
            value="gantt"
            onChange={v => { if (v === 'cal-b2') navigate('cal-b2'); else if (v === 'work-type') navigate('work-type') }}
          />
        </>
      )}
    </div>
  )
}
